import React from 'react';
import styled from 'styled-components';
import { tokens } from '../../utils/theme';

const ModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 1300;
    background: rgba(3, 5, 10, 0.84);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    animation: fadeIn 0.15s ease-out;

    @keyframes fadeIn {
        from { opacity: 0; transform: scale(0.99); }
        to { opacity: 1; transform: scale(1); }
    }
`;

const ModalBox = styled.div`
    width: 660px;
    max-width: 95vw;
    max-height: 90vh;
    background: ${props => props.theme.palette.mode === 'dark'
        ? 'linear-gradient(180deg, #101524 0%, #0a0d16 100%)'
        : '#ffffff'};
    border: 1px solid ${props => props.theme.palette.mode === 'dark'
        ? 'rgba(128, 203, 196, 0.2)'
        : 'rgba(0, 0, 0, 0.1)'};
    border-radius: 16px;
    box-shadow: 0 25px 60px rgba(0, 0, 0, 0.7), 0 0 35px rgba(128, 203, 196, 0.06);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    color: ${props => props.theme.palette.text.primary};
    position: relative;
`;

const CloseButton = styled.button`
    position: absolute;
    top: 16px;
    right: 18px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'};
    color: ${props => tokens(props.theme.palette.mode).grey[400]};
    width: 30px;
    height: 30px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 15px;
    transition: all 0.15s ease;
    z-index: 10;

    &:hover {
        background: rgba(239, 83, 80, 0.15);
        color: #ef5350;
        border-color: rgba(239, 83, 80, 0.3);
        transform: scale(1.05);
    }
`;

const CenteredHero = styled.div`
    padding: 26px 24px 20px 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    border-bottom: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'};
    background: ${props => props.theme.palette.mode === 'dark'
        ? 'radial-gradient(ellipse at 50% 0%, rgba(128, 203, 196, 0.12), transparent 70%)'
        : 'radial-gradient(ellipse at 50% 0%, rgba(15, 118, 110, 0.05), transparent 70%)'};
`;

const HeaderFlag = styled.div`
    font-size: 32px;
    line-height: 1;
    margin: 0 0 14px 0;
    user-select: none;
    filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.25));
    transition: transform 0.2s ease;
    cursor: default;

    &:hover {
        transform: scale(1.08);
    }
`;

const HealthScoreCard = styled.div`
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    padding: 10px 28px 12px 28px;
    border-radius: 12px;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)'};
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.22)' : 'rgba(15, 118, 110, 0.18)'};
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.04);
`;

const HealthLabel = styled.span`
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1.8px;
    color: ${props => tokens(props.theme.palette.mode).grey[400]};
    margin-bottom: 4px;
`;

const HealthScoreValueRow = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: center;
`;

const HealthScoreNum = styled.span`
    font-size: 34px;
    font-weight: 800;
    line-height: 1;
    color: #80cbc4;
    font-variant-numeric: tabular-nums;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    letter-spacing: -0.8px;
    text-shadow: 0 0 16px rgba(128, 203, 196, 0.35);
`;

const HealthScoreMax = styled.span`
    font-size: 19px;
    font-weight: 700;
    color: ${props => props.theme.palette.mode === 'dark' ? '#94a3b8' : '#64748b'};
    margin-left: 4px;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.2px;
`;

const HealthScoreBar = styled.div`
    width: 140px;
    height: 4px;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'};
    border-radius: 999px;
    overflow: hidden;
    margin-top: 8px;
`;

const HealthScoreBarFill = styled.div`
    height: 100%;
    width: ${props => Math.min(100, Math.max(0, props.$width))}%;
    background: linear-gradient(90deg, #26a69a, #80cbc4);
    border-radius: 999px;
    box-shadow: 0 0 8px rgba(128, 203, 196, 0.5);
    transition: width 0.4s ease;
`;

const ModalContent = styled.div`
    padding: 18px 22px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;

    &::-webkit-scrollbar {
        width: 5px;
    }
    &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.15);
        border-radius: 3px;
    }
`;

const BatteryRowsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const BatteryRow = styled.div`
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.015)'};
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.06)'};
    border-radius: 8px;
    padding: 10px 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    transition: background 0.12s ease, border-color 0.12s ease;

    &:hover {
        background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)'};
        border-color: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'};
    }
`;

const LeftMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 250px;
`;

const RegimeTitle = styled.span`
    font-size: 13px;
    font-weight: 700;
    color: ${props => props.theme.palette.mode === 'dark' ? '#f0f6fc' : '#0b1220'};
`;

const DescriptorPill = styled.span`
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 4px;
    background: ${props => props.$bg || 'rgba(128, 203, 196, 0.12)'};
    color: ${props => props.$color || '#80cbc4'};
    letter-spacing: 0.1px;
    white-space: nowrap;
`;

const GaugeGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const BatteryHousing = styled.div`
    display: flex;
    align-items: center;
    gap: 3px;
`;

const BatteryFrame = styled.div`
    width: 110px;
    height: 12px;
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'};
    border-radius: 3px;
    padding: 1.5px;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.05)'};
    display: flex;
    gap: 2px;
`;

const BatteryBlock = styled.div`
    flex: 1;
    height: 100%;
    border-radius: 1px;
    background: ${props => props.$active ? props.$color : 'transparent'};
`;

const BatteryNipple = styled.div`
    width: 2.5px;
    height: 6px;
    border-radius: 0 1px 1px 0;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)'};
`;

const PercentageText = styled.span`
    font-size: 13px;
    font-weight: 700;
    min-width: 36px;
    text-align: right;
    font-variant-numeric: tabular-nums;
    color: ${props => props.$color || '#80cbc4'};
`;

const getFallbackDescriptor = (id, score) => {
    switch (id) {
        case 'growth':
            if (score >= 60) return 'Strong Expansion';
            if (score >= 35) return 'Moderate Growth';
            return 'Subdued Growth';
        case 'inflation':
            if (score >= 60) return 'High Inflation';
            if (score >= 35) return 'Moderate Inflation';
            return 'Low Inflation';
        case 'overheating':
            if (score >= 55) return 'Demand Overheating';
            if (score >= 30) return 'Moderate Strain';
            return 'Normal Capacity';
        case 'stagflation':
            if (score >= 55) return 'High Stagflation Risk';
            if (score >= 30) return 'Moderate Stagflation';
            return 'Low Stagflation';
        case 'recession':
            if (score >= 50) return 'High Contraction Risk';
            if (score >= 25) return 'Moderate Slowdown';
            return 'Low Contraction Risk';
        case 'deflation':
            if (score >= 45) return 'Deflation Risk';
            if (score >= 20) return 'Disinflationary';
            return 'Minimal Deflation';
        default:
            return '';
    }
};

export const EconomicBatteriesModal = ({ isOpen, onClose, country }) => {
    if (!isOpen || !country) return null;

    // Batteries prepared directly by the backend algorithm (ordered by charge descending)
    const batteries = country.sortedBatteries || country.economicBatteries || [];
    const countryFlag = country.flag || (country.code === 'USD' || country.id === 'US' || !country.name ? '🇺🇸' : '');
    const countryName = country.name || 'United States';
    const scoreNum = country.healthScore10 !== undefined ? Number(country.healthScore10).toFixed(1) : '7.4';
    const scoreMax = country.healthScoreMax || 10;
    const barWidth = country.healthScore !== undefined ? country.healthScore : 74;

    return (
        <ModalOverlay onClick={onClose}>
            <ModalBox onClick={e => e.stopPropagation()}>
                <CloseButton onClick={onClose} aria-label="Close">✕</CloseButton>

                {/* Centered Modern Hero Section */}
                <CenteredHero>
                    {countryFlag && (
                        <HeaderFlag title={`${countryName} - Economic Health`} aria-label={countryName}>
                            {countryFlag}
                        </HeaderFlag>
                    )}

                    <HealthScoreCard>
                        <HealthLabel>Health Score</HealthLabel>
                        <HealthScoreValueRow>
                            <HealthScoreNum>{scoreNum}</HealthScoreNum>
                            <HealthScoreMax>/{scoreMax}</HealthScoreMax>
                        </HealthScoreValueRow>
                        <HealthScoreBar>
                            <HealthScoreBarFill $width={barWidth} />
                        </HealthScoreBar>
                    </HealthScoreCard>
                </CenteredHero>

                {/* Content */}
                <ModalContent>
                    {/* The 6 Clean Battery Rows prepared by backend algorithm */}
                    <BatteryRowsList>
                        {batteries.map(battery => {
                            const color = battery.color || '#80cbc4';
                            const activeBlocks = battery.activeBlocks !== undefined
                                ? battery.activeBlocks
                                : Math.min(5, Math.max(0, Math.round((battery.score || 0) / 20)));
                            const descriptor = battery.descriptor || getFallbackDescriptor(battery.id, battery.score || 0);

                            return (
                                <BatteryRow key={battery.id}>
                                    <LeftMeta>
                                        <RegimeTitle>{battery.name}</RegimeTitle>
                                        {descriptor && (
                                            <DescriptorPill
                                                $bg={`${color}15`}
                                                $color={color}
                                            >
                                                {descriptor}
                                            </DescriptorPill>
                                        )}
                                    </LeftMeta>

                                    <GaugeGroup>
                                        <BatteryHousing>
                                            <BatteryFrame>
                                                {[1, 2, 3, 4, 5].map(b => (
                                                    <BatteryBlock
                                                        key={b}
                                                        $active={b <= activeBlocks}
                                                        $color={color}
                                                    />
                                                ))}
                                            </BatteryFrame>
                                            <BatteryNipple />
                                        </BatteryHousing>
                                        <PercentageText $color={color}>
                                            {battery.percentage || `${battery.score}%`}
                                        </PercentageText>
                                    </GaugeGroup>
                                </BatteryRow>
                            );
                        })}
                    </BatteryRowsList>
                </ModalContent>
            </ModalBox>
        </ModalOverlay>
    );
};

export default EconomicBatteriesModal;
